import { Info as InfoIcon, Search as SearchIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useLogFetch } from "../hooks/useLogsFetch";
import LogRow from "./LogRow";
import LogsFilter from "./LogsFilter";

const EmptyState = ({ message, icon }) => (
    <Box sx={{ textAlign: "center", my: 4, py: 4 }}>
        {icon}
        <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
            {message}
        </Typography>
    </Box>
);

const Logs = ({ startDate, endDate }) => {
    const { logs, loading, error } = useLogFetch(startDate, endDate);
    const [filters, setFilters] = useState({
        method: "",
        status: { success: true, error: true },
    });

    const filteredLogs = useMemo(() => {
        return logs.filter((log) => {
            if (filters.method && log.method !== filters.method) {
                return false;
            }

            if (log.error && !filters.status.error) {
                return false;
            }

            if (!log.error && !filters.status.success) {
                return false;
            }

            if (filters.searchTerm) {
                const searchLower = filters.searchTerm.toLowerCase();
                const methodMatch =
                    log.method &&
                    log.method.toLowerCase().includes(searchLower);
                const idMatch =
                    log._id && log._id.toLowerCase().includes(searchLower);
                const errorMatch =
                    log.error && log.error.toLowerCase().includes(searchLower);

                let detailsMatch = false;

                if (log.input) {
                    try {
                        const inputStr = JSON.stringify(
                            log.input
                        ).toLowerCase();
                        if (inputStr.includes(searchLower)) {
                            detailsMatch = true;
                        }
                    } catch (e) {}
                }

                if (log.kwargs) {
                    try {
                        const kwargsStr = JSON.stringify(
                            log.kwargs
                        ).toLowerCase();
                        if (kwargsStr.includes(searchLower)) {
                            detailsMatch = true;
                        }
                    } catch (e) {}
                }

                if (!(methodMatch || idMatch || errorMatch || detailsMatch)) {
                    return false;
                }
            }

            return true;
        });
    }, [logs, filters]);

    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
    };

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.method) count++;
        if (!filters.status.success || !filters.status.error) count++;
        if (filters.searchTerm) count++;
        return count;
    }, [filters]);

    const renderLoadingSkeleton = () => (
        <Box sx={{ mt: 3 }}>
            {[...Array(3)].map((_, index) => (
                <Skeleton
                    key={index}
                    variant="rectangular"
                    height={53}
                    sx={{ mb: 1, borderRadius: 1 }}
                />
            ))}
        </Box>
    );

    const renderLogsTable = () => (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">Logs</Typography>
                    <Typography variant="body2" color="text.secondary">
                        ({filteredLogs.length} of {logs.length} entries)
                    </Typography>

                    {activeFilterCount > 0 && (
                        <Chip
                            size="small"
                            label={`${activeFilterCount} filter${
                                activeFilterCount > 1 ? "s" : ""
                            } active`}
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Stack>

                <LogsFilter
                    logs={logs}
                    onApplyFilters={handleApplyFilters}
                    activeFilters={filters}
                />
            </Box>

            <TableContainer
                component={Paper}
                elevation={0}
                variant="outlined"
                sx={{
                    overflowX: "auto",
                    borderRadius: 1,
                    "& .MuiTable-root": {
                        borderCollapse: "collapse",
                        tableLayout: "fixed",
                    },
                    "& .MuiTableHead-root": {
                        backgroundColor: (theme) => theme.palette.grey[50],
                    },
                    "& .MuiTableHead-root .MuiTableRow-root": {
                        borderBottom: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                        fontWeight: 600,
                        color: (theme) => theme.palette.text.secondary,
                        borderBottom: "none",
                    },
                }}
            >
                <Table aria-label="logs table" size="medium">
                    <colgroup>
                        <col style={{ width: "40px" }} />
                        <col style={{ width: "25%" }} />
                        <col style={{ width: "45%" }} />
                        <col style={{ width: "15%" }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell className="action-cell" />
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredLogs.length > 0 ? (
                            filteredLogs.map((log) => (
                                <LogRow key={log._id} log={log} />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    align="center"
                                    sx={{ py: 3 }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No logs found matching current filters
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );

    if (!startDate || !endDate) {
        return (
            <EmptyState
                message="Please select start and end dates to view logs"
                icon={<InfoIcon color="info" sx={{ fontSize: 40 }} />}
            />
        );
    }

    if (loading) {
        return renderLoadingSkeleton();
    }

    if (error) {
        return (
            <Alert
                severity="error"
                variant="outlined"
                sx={{ mt: 3 }}
                action={<IconButton size="small">Retry</IconButton>}
            >
                {error}
            </Alert>
        );
    }

    if (logs.length === 0) {
        return (
            <EmptyState
                message="No logs found for the selected date range"
                icon={
                    <SearchIcon
                        color="action"
                        sx={{ fontSize: 40, opacity: 0.5 }}
                    />
                }
            />
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                mt: 3,
                overflowX: "hidden",
                "& .action-cell": {
                    width: "40px !important",
                    maxWidth: "40px !important",
                    minWidth: "40px !important",
                    padding: "0 8px !important",
                },
            }}
        >
            {renderLogsTable()}
        </Box>
    );
};

export default Logs;
